import React from 'react';
import { useTranslation } from 'react-i18next';
import FeatherIcon from '../common/FeatherIcon';
import { lensName, lensStyle } from './lenses';
import './lens.css';

/**
 * Sidebar pieces for an active lens. The sidebar renders the lens's groups
 * with its own section renderer (so they look, collapse and fly out like any
 * other section); these are the two pieces around them.
 */

/**
 * "LENS / AI SOC" card with the lens colour tile. `rail` = icon-rail layout.
 * When the lens was picked from the user's role rather than by them, the
 * caption says so, so nobody wonders why their sidebar changed.
 */
export const LensNavCard = ({ lens, chosenForRole = false, rail = false }) => {
  const { t } = useTranslation('nav');
  if (!lens || lens.id === 'all') return null;
  const name = lensName(lens, t);
  const caption = chosenForRole ? t('nav:lens.chosenForRole') : t('nav:lens.caption');
  return (
    <div
      className={`lens-nav-card${rail ? ' rail' : ''}`}
      style={lensStyle(lens)}
      title={rail ? `${caption}: ${name}` : undefined}
    >
      <span className={`lens-tile${lens.abbr.length > 3 ? ' long' : ''}`} style={{ background: lens.color }} aria-hidden="true">{lens.abbr}</span>
      <span className="lens-nav-card-text">
        <span className="lens-nav-card-caption">{caption}</span>
        <span className="lens-nav-card-name">{name}</span>
      </span>
    </div>
  );
};

/**
 * The expander that keeps every module reachable under a lens.
 * `count` is how many nav pages sit behind it.
 */
export const AllModulesToggle = ({ open, onToggle, count, controls, rail = false }) => {
  const { t } = useTranslation('nav');
  const more = Number.isFinite(count) && count > 0 ? t('nav:lens.moreCount', { count }) : '';
  return (
    <button
      type="button"
      className={`lens-all-modules${rail ? ' rail' : ''}${open ? ' open' : ''}`}
      aria-expanded={open}
      aria-controls={controls}
      title={rail ? [t('nav:lens.allModules'), more].filter(Boolean).join(' · ') : undefined}
      onClick={onToggle}
    >
      <FeatherIcon name="grid" size={rail ? 18 : 16} className="lens-all-modules-icon" aria-hidden="true" />
      <span className="lens-all-modules-label">{t('nav:lens.allModules')}</span>
      {more && <span className="lens-all-modules-count">{more}</span>}
      <FeatherIcon
        name={open ? 'chevron-down' : 'chevron-right'}
        size={14}
        className="lens-all-modules-chevron"
        aria-hidden="true"
      />
    </button>
  );
};
